// ────────────────────────────────────────────────────────────────────────────
// OCR results QA panel.
//
// Renders the most recent OCR / classification outcomes from the in-memory
// ocrResultStore ring buffer. Used inside the Live Scan modal and the
// Settings → Live Scan diagnostics card so beta testers can confirm what
// the classifier saw and what the per-screen OCR module produced, on a
// per-frame basis.
//
// Production-safe: no raw frame bytes, no dataUrls, no PII beyond what
// already flows through scanTelemetry.
// ────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import {
  getOcrResultSnapshot,
  resetOcrResults,
  subscribeOcrResults,
  type OcrResult,
  type OcrResultStoreSnapshot,
} from "@/lib/ocrResultStore";

function formatRelative(ts: number, now: number): string {
  const diff = Math.max(0, now - ts);
  if (diff < 1500) return "just now";
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function statusTone(status: OcrResult["status"]): {
  label: string;
  cls: string;
  border: string;
} {
  switch (status) {
    case "accepted":
      return {
        label: "Accepted",
        cls: "ingest-stat-value--ok",
        border: "hsl(142 80% 35% / 0.5)",
      };
    case "duplicate":
      return {
        label: "Duplicate",
        cls: "ingest-stat-value--warn",
        border: "hsl(43 96% 60% / 0.45)",
      };
    case "empty":
      return {
        label: "Empty",
        cls: "ingest-stat-label",
        border: "var(--surface-elev-border)",
      };
    default:
      return {
        label: "Rejected",
        cls: "ingest-failure-reason",
        border: "hsl(354 78% 56% / 0.45)",
      };
  }
}

function useOcrSnapshot(): OcrResultStoreSnapshot {
  const [snap, setSnap] = useState<OcrResultStoreSnapshot>(() =>
    getOcrResultSnapshot(),
  );
  useEffect(() => {
    return subscribeOcrResults(() => setSnap(getOcrResultSnapshot()));
  }, []);
  return snap;
}

/**
 * Compact list of the most recent OCR results. Designed to drop into the
 * Live Scan modal as an expandable QA card AND into the Settings
 * diagnostics surface.
 */
export function OcrResultsPanel({
  testid = "ocr-results-panel",
  maxRows = 8,
}: {
  testid?: string;
  maxRows?: number;
}) {
  const snap = useOcrSnapshot();
  const now = Date.now();
  const rows = snap.results.slice().reverse().slice(0, maxRows);

  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{
        background: "var(--surface-elev-1)",
        border: "1px solid var(--surface-elev-border)",
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          Recent OCR results
        </p>
        <span
          className="ingest-stat-label text-[10px] tabular-nums"
          data-testid="ocr-results-counter"
        >
          {snap.results.length} shown · {snap.totalRecorded} total
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="ingest-stat-hint text-[11px] leading-relaxed">
          No OCR results recorded yet. Start a Live Scan or import a screenshot
          to populate this list.
        </p>
      ) : (
        <div className="space-y-2" data-testid="ocr-results-list">
          {rows.map((r) => (
            <OcrResultRow key={r.id} result={r} now={now} />
          ))}
        </div>
      )}

      {snap.totalRecorded > 0 ? (
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => resetOcrResults()}
            data-testid="button-ocr-results-clear"
            className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
            style={{
              background: "var(--surface-elev-2)",
              border: "1px solid var(--surface-elev-border)",
              color: "hsl(var(--foreground))",
            }}
          >
            Clear
          </button>
        </div>
      ) : null}
    </div>
  );
}

function OcrResultRow({ result, now }: { result: OcrResult; now: number }) {
  const tone = statusTone(result.status);
  const identity = result.name
    ? `${result.name}${result.cp !== null ? ` · CP ${result.cp}` : ""}`
    : result.speciesId !== null
      ? `Species ${result.speciesId}${result.cp !== null ? ` · CP ${result.cp}` : ""}`
      : null;
  return (
    <div
      className="rounded-xl px-3 py-2 text-[11px]"
      style={{
        background: "var(--surface-elev-2)",
        border: `1px solid ${tone.border}`,
      }}
      data-testid={`ocr-result-row-${result.id}`}
      data-status={result.status}
      data-source={result.source}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono uppercase text-[10px] tracking-wider ingest-source-tier px-1.5 rounded">
          {result.source}
        </span>
        <span className="ingest-stat-label text-[10px] font-mono">
          {result.rawScreen}
          {result.routedScreen !== result.rawScreen
            ? ` → ${result.routedScreen}`
            : ""}
          {result.routePromoted ? " (promoted)" : ""}
        </span>
        <span className={`ml-auto ${tone.cls} font-mono text-[10px] uppercase`}>
          {tone.label}
        </span>
      </div>

      {identity ? (
        <p className="mt-1 font-mono">{identity}</p>
      ) : null}

      <div className="ingest-stat-hint text-[10px] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {result.ocrConfidence !== null ? (
          <span>OCR conf {Math.round(result.ocrConfidence)}</span>
        ) : null}
        {result.classifierConfidence !== null ? (
          <span>cls conf {Math.round(result.classifierConfidence)}</span>
        ) : null}
        <span>{formatRelative(result.ts, now)}</span>
        {result.routeReason ? <span>route: {result.routeReason}</span> : null}
      </div>

      {result.spriteCaptured ? (
        <p className="text-[10px] mt-1 ingest-stat-value--ok">
          ◉ observed sprite captured & cached
        </p>
      ) : result.spriteSilhouetteFallback ? (
        <p className="text-[10px] mt-1 ingest-stat-label">
          ○ silhouette fallback (no observed crop)
        </p>
      ) : null}

      {result.boxMetrics ? (
        <p
          className="text-[10px] mt-1 ingest-stat-hint flex flex-wrap gap-x-3 gap-y-0.5"
          data-testid={`ocr-result-box-metrics-${result.id}`}
        >
          <span>cells {result.boxMetrics.rowsParsed}</span>
          <span className="ingest-stat-value--ok">
            accepted {result.boxMetrics.accepted}
          </span>
          {result.boxMetrics.duplicates > 0 ? (
            <span>dupes {result.boxMetrics.duplicates}</span>
          ) : null}
          {result.boxMetrics.lowConfidence > 0 ? (
            <span className="ingest-stat-value--warn">
              review {result.boxMetrics.lowConfidence}
            </span>
          ) : null}
          {result.boxMetrics.spritesCaptured > 0 ? (
            <span className="ingest-stat-value--ok">
              sprites {result.boxMetrics.spritesCaptured}
            </span>
          ) : null}
        </p>
      ) : null}

      {result.dropReason ? (
        <p className="text-[10px] mt-1 ingest-failure-reason">
          drop: {result.dropReason}
        </p>
      ) : null}

      {result.warnings.length > 0 ? (
        <p className="text-[10px] mt-1 ingest-stat-value--warn break-all">
          warn: {result.warnings.join(" | ")}
        </p>
      ) : null}

      {result.rows.length > 0 ? (
        <div
          className="mt-1.5 rounded-lg overflow-hidden"
          style={{ border: "1px solid var(--surface-elev-border)" }}
          data-testid={`ocr-result-rows-${result.id}`}
        >
          {result.rows.map((row, idx) => {
            const label =
              row.name
              ?? (row.speciesId !== null ? `#${row.speciesId}` : "(unknown)");
            return (
              <div
                key={idx}
                className="flex items-center gap-2 px-2 py-1 text-[10px] border-b last:border-b-0"
                style={{ borderColor: "var(--surface-elev-border)" }}
                data-testid={`ocr-result-row-entry-${result.id}-${idx}`}
              >
                <span className="font-mono">{label}</span>
                {row.cp !== null ? (
                  <span className="ingest-stat-hint">CP {row.cp}</span>
                ) : null}
                {row.isShiny ? (
                  <span className="ingest-stat-value--warn">✦</span>
                ) : null}
                <span
                  className={
                    "ml-auto font-mono uppercase text-[9px] "
                    + (row.accepted
                      ? "ingest-stat-value--ok"
                      : "ingest-failure-reason")
                  }
                >
                  {row.accepted ? "ok" : (row.dropReason ?? "drop")}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {result.flushOutcome ? (
        <p className="ingest-stat-hint text-[10px] mt-1">
          flush {result.flushOutcome.endpoint}
          {result.flushOutcome.status !== null
            ? ` · status ${result.flushOutcome.status}`
            : ""}
          {result.flushOutcome.accepted !== null
            ? ` · accepted ${result.flushOutcome.accepted}`
            : ""}
        </p>
      ) : null}
    </div>
  );
}
